/* GLOBAL BETWEEN DIFF COMPONENTS */

/**
 * For setting up jump-links to sectioned components,
 * such as tabbed content or accordions
 */
export const PAGE_HASH_DATA = {
  windowHashValue: window.location.hash.replace('#', ''),
  allAnchorHashElements: document.querySelectorAll('a[href^=\\#]'),
  get allAnchorHashValues() {
    return this.allAnchorHashElements != null
      ? [...this.allAnchorHashElements]
          .map(({ hash }) => hash)
          .filter((str) => str != '') // don't want empties
      : '';
  },
};

/**
 * Given an element, scrolls to its parent container
 * @param {Node Element} element - clicked/active element
 */
export const scrollToContainer = (element, containerClass) => {
  if (!element || containerClass == '') {
    return;
  }

  const VIEWHEIGHT = document.documentElement.clientHeight;
  const OFFSET = VIEWHEIGHT * 0.1;

  let containingElement = element.closest(containerClass);
  if (!containingElement) {
    return;
  }

  let elementTop = containingElement.getBoundingClientRect().top;

  // don't jump to if already in ~ top->center view
  if (elementTop >= 0 && elementTop <= (window.innerHeight || VIEWHEIGHT) / 2) {
    return;
  }

  window.scrollTo({
    behavior: 'smooth',
    top: elementTop - document.body.getBoundingClientRect().top - OFFSET,
  });
};

/* CLASS MANIPULATIONS */

/**
 * @param {Element} el
 * @param {string} cssClass
 * @returns success or failure
 */
export const addClass = (el, cssClass) => {
  if (!el.classList.contains(cssClass)) {
    el.classList.add(cssClass);
    return true;
  }

  return false;
};

/**
 * @param {Element} el
 * @param {string} cssClass
 * @returns success or failure
 */
export const removeClass = (el, cssClass) => {
  if (el.classList.contains(cssClass)) {
    el.classList.remove(cssClass);
    return true;
  }

  return false;
};

/**
 * Toggles class for an element node by utilizing other utility functions
 * @param {Element} el
 * @param {string} cssClass
 * @calls addClass || removeClass
 */
export const toggleClass = (el, cssClass) => {
  addClass(el, cssClass) || removeClass(el, cssClass);
};

/**
 * Toggles multiple classes for a single element node
 * @param {Element} el
 * @param {array} classList - list of strings
 * @calls addClass || removeClass
 */
export const toggleClasses = (el, classList) => {
  for (let c = 0; c < classList.length; c++) {
    addClass(el, classList[c]) || removeClass(el, classList[c]);
  }
};

/**
 * @param {Element} el - element to be wrapped
 * @param {Element} wrapper - wrapper node to be created to wrap around el
 * @param {string} wrapperClass - class(es) to add to the wrapper
 */
export const wrapElement = (el, wrapper, wrapperClass) => {
  el.parentNode.insertBefore(wrapper, el);
  wrapper.appendChild(el);
  addClass(wrapper, wrapperClass);
};

/**
 * Toggles a binary attribute of an element node
 * @param {Element} el
 * @param {string} attr - name of element's attribute to target
 * @returns success or failure
 */
export const toggleAttribute = (el, attr) => {
  let currentValue = el.getAttribute(attr);

  if (currentValue == 'true') {
    el.setAttribute(attr, 'false');
    return true;
  } else if (currentValue == 'false') {
    el.setAttribute(attr, 'true');
    return true;
  }

  return false;
};

/**
 * Creates a dynamically sized slide up/down effect for toggleable content.
 * Does not control open/close class to simplify cases with children focus keeping state open
 * @param {Object} e - Event that triggered or a target element for scope
 * @param {string} parentContainer - identifier for the parent container. (Eg: '.class')
 * @param {string} toggleContent - identifier for the content container to animate
 * @param {string} cssClass - class to look for whether container is open/closed. Default is 'is-open'
 * @param {number} paddingBuffer - used when expanding to account for unknown padding transition amounts
 */
export const slideDownTogglableSection = (
  e = null,
  parentContainer,
  toggleContent,
  cssClass = 'is-open',
  paddingBuffer = 128 // ~4x rhythm
) => {
  // check if event or element
  const targetElement = e instanceof Event ? e.target : e;

  // get parent toggle-section to locate content & apply/remove open class
  let sectionContainer = targetElement.closest(parentContainer);
  if (!sectionContainer) {
    console.debug('Unable to find the sectionContainer');
    return;
  }

  let contentContainer = sectionContainer.querySelector(toggleContent);
  if (!contentContainer) {
    return;
  }

  if (sectionContainer.classList.contains(cssClass)) {
    // close
    // store transition for animation later; safety precaution
    var transitions = contentContainer.style.transition;
    contentContainer.style.transition = '';

    // explicitly set height to its current pixel height to avoid transitioning from 'auto'
    contentContainer.style.maxHeight = contentContainer.scrollHeight + 'px';

    // on next animation frame re-add transitions
    window.requestAnimationFrame(function () {
      contentContainer.style.transition = transitions;

      // on next animation frame proceed transitioning from max-height -> 0
      window.requestAnimationFrame(function () {
        contentContainer.style.maxHeight = 0 + 'px';
        contentContainer.style.removeProperty('max-height');
      });
    });
  } else {
    // expand
    // At this point scrollHeight may not know about any 'open' state padding or margin;
    // depends on the order this is called in with the class toggling.
    // So, to be safe, apply some buffer
    contentContainer.style.maxHeight =
      contentContainer.scrollHeight + paddingBuffer + 'px';
  }
};
